import createStore from 'unistore';
import devtools from 'unistore/devtools';
import pick from 'lodash/fp/pick';
import { shape, number, string, bool } from 'prop-types';

import apiClient from 'api/ApiClient';
import { gon } from 'common/gon';
import segment from 'common/segment';
import taxonQuantityLimit from 'common/utils/taxonQuantityLimit';
import localStorage from 'common/local_storage';

import {
  filterRemovedSelectedItems,
  getInitialSelectedPods,
  getMajoritySelectedNicotineStrength,
} from './utils';
import {
  selectCartLineItems,
  selectEnableEditFlow,
  selectNicotineStrengths,
  selectSelectedPodsTotalQuantity,
} from './selectors';

const STORAGE_KEY = 'build_your_plan_local_storage';

let store;

let initialState = {
  // Currently logged in user
  user: gon('user'),

  minimumSubscriptionItems: gon('minimumSubscriptionItems'),

  // A Boolean flag to instruct the application on wether the user has begun the checkout
  // process, if they have they will be redirected to the pods component page and shown any errors
  // that exist on their cart
  redirectToCheckout: false,

  removedPods: [],

  // pods: an object keeping track of selected pods for the autoship plan,
  // keyed by pod product ID. takes shape of product with appended quantity
  selectedPods: {},

  // All pods return from API
  pods: [],

  selectedAddOns: {},

  selectedNicotineStrength: null,

  // A potential error code string from lib/build_your_plan which will trigger
  // display of error state modal. Null-ish if no error
  error: null,

  featureFlags: {},

  podsLimit: taxonQuantityLimit('pods'),

  strikethroughPrices: gon(
    'current_store.preferences.show_strikethrough_prices'
  ),

  subscription: {},
};

const storedState = localStorage.getItem(STORAGE_KEY);

if (storedState) {
  try {
    initialState = {
      ...initialState,
      ...JSON.parse(storedState),
    };
  } catch (e) {
    // Do nothing if they have an invalid state stored in LocalStorage.
    // initialState will have the defaults defined above.
  }
}

if (!store) {
  store = ['production', 'test'].includes(gon('env.RAILS_ENV'))
    ? createStore(initialState)
    : devtools(createStore(initialState));
}

const serializableStoreProperties = [
  'selectedAddOns',
  'selectedPods',
  'nicotineStrength',
];

store.subscribe(state => {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(pick(serializableStoreProperties)(state))
  );
});

export const selectedPodProps = shape({
  id: string.isRequired,
  image_url: string.isRequired,
  is_sold_out: bool.isRequired,
  is_subscribable: bool,
  name: string.isRequired,
  nicotine_strength_percent: string.isRequired,
  offeringId: number.isRequired,
  pod_quantity: number.isRequired,
  price: number.isRequired,
  product_listing_image_url: string.isRequired,
  quantity: number.isRequired,
  review_score: number,
  sku: string.isRequired,
});

const offeringProps = shape({
  id: number.isRequired,
  variantSku: string.isRequired,
});
export const selectedAddOnProps = shape({
  // The selected offering
  offering: offeringProps.isRequired,
  product: shape({
    // TODO: This will be updated in the future to have multiple offerings
    offering: offeringProps,
    price: number.isRequired,
  }).isRequired,
  quantity: number.isRequired,
});

const fetchPodsAndSubscription = async (state, editFlowEnabled) => {
  const {
    pods,
    flavor_banned_subscribed_pods: flavorBannedSubscribedPods,
    unavailable_subscribed_pods: unavailableSubscribedPods,
  } = await apiClient.fetchSubscribablePods();
  const { subscription = {} } = await (editFlowEnabled
    ? gon('user.subscribed') && apiClient.fetchSubscription()
    : {});

  const { selectedPods, removedPods } = getInitialSelectedPods({
    flavorBannedSubscribedPods,
    pods,
    selectedPods: state.selectedPods,
    subscription,
    unavailableSubscribedPods,
  });

  const subscriptionStrength = getMajoritySelectedNicotineStrength(
    selectedPods
  );

  return {
    pods,
    selectedPods,
    removedPods,
    subscription,
    selectedNicotineStrength: subscriptionStrength
      ? subscriptionStrength
      : selectNicotineStrengths({ pods })[0],
  };
};

const actions = store => ({
  clearErrors() {
    return { error: null };
  },

  updatePodQuantity(state, { pod, quantity }) {
    const selectedPods = {
      ...state.selectedPods,
    };

    if (!selectedPods[pod.id] || selectedPods[pod.id].quantity < quantity) {
      segment.track('AS Builder Product added', {
        ...pod,
        quantity,
      });
    }

    selectedPods[pod.id] = {
      ...pod,
      quantity,
    };

    return { selectedPods: filterRemovedSelectedItems(selectedPods) };
  },

  updateAddOnQuantity(state, { product, offering, quantity }) {
    const selectedAddOns = {
      ...state.selectedAddOns,
      [offering.id]: {
        product,
        offering,
        quantity,
      },
    };

    return {
      selectedAddOns: filterRemovedSelectedItems(selectedAddOns),
    };
  },

  updateNicotineStrength(_state, { nicotineStrength }) {
    segment.track('AS Builder Pod Filter Selected', {
      value: nicotineStrength,
    });
    return { selectedNicotineStrength: nicotineStrength };
  },

  async updateSubscription(
    { subscription, changedFields },
    apiClient = apiClient
  ) {
    const res = await apiClient.updateSubscription(subscription, changedFields);

    if (res.error) {
      return {
        error: res.error,
      };
    }

    return { subscription: res.subscription };
  },

  async checkout(state) {
    // Reset redirectToCheckout so we dont get into redirect hell
    store.setState({ redirectToCheckout: false });

    // Check errors up ahead, regardless of logged in/out state
    const podQuantity = selectSelectedPodsTotalQuantity(state);
    const res = await apiClient.checkBuildYourPlanErrorState(podQuantity);
    if (res.error !== null) {
      return {
        redirectToCheckout: false,
        error: res.error,
      };
    }

    if (state.user) {
      const lineItems = selectCartLineItems(state);

      // Make the call to clear cart and add the selected pods
      const res = await apiClient.updateCartAndClear(lineItems);
      setTimeout(() => (window.location.href = '/checkout'));

      // Update the state with the returned order.
      return { order: res.order };
    }

    // If the user is NOT signed in, we forward them to the sign in page,
    // upon signing in they will be redirected back to build-your-plan
    // NOTE: this does skip AV, I realize now this may not be the best path,
    // we may need a new endpoint for this..........
    // This is in a setTimeout so that the function can return redirectToCheckout: true
    // to the state
    setTimeout(
      () => (window.location.href = '/signin?redirect=/build-your-plan')
    );

    return { redirectToCheckout: true };
  },

  async fetchInitialData(state) {
    const podQuantity = selectSelectedPodsTotalQuantity(state);

    const { features } = await apiClient.fetchSplitTreatments([
      'nagini-enable-edit-flow',
      'nagini-show-add-ons-page',
    ]);
    const featureFlagsState = {
      featureFlags: features,
    };
    const editFlowEnabled = selectEnableEditFlow(featureFlagsState);

    const [checkErrorResponse, podsAndSubscriptionState] = await Promise.all([
      apiClient.checkBuildYourPlanErrorState(podQuantity),
      fetchPodsAndSubscription(state, editFlowEnabled),
    ]);

    return {
      error: checkErrorResponse.error,
      ...featureFlagsState,
      ...podsAndSubscriptionState,
      user: gon('user'),
    };
  },
});

export { store, actions };
